import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import {
  Component,
  computed,
  inject,
  input,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { buildWordBoundaryMatcher, firstInitial, foldForSearch } from '@shared';
import * as fromGenerated from '../../../_generated';
import { AssistantShareAccessRowComponent } from './assistant-share-access-row.component';

const MAX_RESULTS_TO_SHOW = 5;

/** A pickable invite target — a user or a custom group — for the search dropdown. */
interface InviteCandidate {
  kind: 'user' | 'group';
  id: string;
  initials: string;
  title: string;
  /** Leading qualifier ("Group") for the access-row secondary line. */
  prefix: string;
  /** Trailing detail — email, or "N members". */
  detail: string;
}

/** Uppercase first letter of a group name for its avatar, `-` when blank. */
const groupInitial = (name: string): string => firstInitial(name) || '-';

/**
 * The "invited people" picker shown when the share dropdown is set to "Invited
 * people". Lets the owner invite specific users and custom groups — a search, a
 * results dropdown, and the access list (the skill owner first, then the
 * invited users/groups) — all rendered with the Serious System access-row node.
 */
@Component({
  selector: 'squadbox-assistant-share-invite-people',
  imports: [
    TranslateModule,
    CdkMenu,
    CdkMenuItem,
    CdkMenuTrigger,
    fromSeriousSystem.InputComponent,
    AssistantShareAccessRowComponent,
  ],
  template: `
    <!-- Invite people / groups -->
    <div #invitePeopleContainer>
      <sd-input
        #invitePeopleInput
        type="text"
        [label]="'ASSISTANTS.SHARE.INVITE_LABEL' | translate"
        [placeholder]="'ASSISTANTS.SHARE.INVITE_PLACEHOLDER' | translate"
        [cdkMenuTriggerFor]="invitePeopleDropdownMenu"
        [cdkMenuTriggerData]="{ menuWidth: invitePeopleContainer.clientWidth }"
        (click)="handleInputChange($event); $event.stopPropagation()"
        (input)="handleInputChange($event)"
      />
    </div>

    <!-- Access list: owner first, then invited groups, then invited users -->
    <div class="flex flex-col">
      @if (ownerRow(); as owner) {
        <squadbox-assistant-share-access-row
          [initials]="owner.initials"
          [title]="owner.title"
          [prefix]="'ASSISTANTS.SHARE.ROW_OWNER' | translate"
          [detail]="owner.detail"
        ></squadbox-assistant-share-access-row>
      }
      @for (group of selectedGroups(); track group.id) {
        <squadbox-assistant-share-access-row
          [initials]="groupInitial(group.name)"
          [title]="group.name"
          [prefix]="'ASSISTANTS.SHARE.ROW_GROUP' | translate"
          [detail]="membersLabel(group.memberCount)"
          [removable]="true"
          (remove)="handleClickRemoveGroupByIdCTA(group.id)"
        ></squadbox-assistant-share-access-row>
      }
      @for (user of selectedUsers(); track user.id) {
        <squadbox-assistant-share-access-row
          [initials]="user.initials ?? '-'"
          [title]="user.fullName ?? user.email"
          [detail]="user.email"
          [removable]="true"
          (remove)="handleClickRemoveUserByIdCTA(user.id)"
        ></squadbox-assistant-share-access-row>
      }
    </div>

    <!-- Search results dropdown (users + groups) -->
    <ng-template #invitePeopleDropdownMenu let-menuWidth="menuWidth">
      <div
        cdkMenu
        class="
          flex flex-col w-full
          bg-shades-white shadow-md
          rounded-md border border-neutral-200
          p-1
        "
        [style.width.px]="menuWidth"
      >
        @for (
          candidate of candidatesFilteredByInputValue();
          track candidate.kind + candidate.id
        ) {
          <button
            cdkMenuItem
            class="block w-full text-left rounded-lg hover:bg-primary-500/5"
            (click)="handleClickAddCandidateCTA(candidate)"
          >
            <squadbox-assistant-share-access-row
              [initials]="candidate.initials"
              [title]="candidate.title"
              [prefix]="candidate.prefix"
              [detail]="candidate.detail"
            ></squadbox-assistant-share-access-row>
          </button>
        }
        @if (!candidatesFilteredByInputValue().length) {
          <div class="h-14 flex items-center text-center justify-center">
            <span class="typo-p3">{{
              'ASSISTANTS.SHARE.NO_RESULTS' | translate
            }}</span>
          </div>
        }
      </div>
    </ng-template>
  `,
  styles: [
    `
      @reference '../../../../styles.css';

      :host {
        @apply flex flex-col gap-4;
      }
    `,
  ],
})
export class AssistantShareInvitePeopleComponent {
  private readonly translate = inject(TranslateService);

  public readonly userId = input.required<string>();
  /** The skill's owner, resolved by the builder; null renders no Owner row. */
  public readonly owner = input<fromGenerated.UserSummaryView | null>(null);
  public readonly allUsers = input.required<fromGenerated.UserSummaryView[]>();
  public readonly allGroups =
    input.required<fromGenerated.GroupSummaryView[]>();
  public readonly selectedUsersIds = model.required<string[]>();
  public readonly selectedGroupsIds = model.required<string[]>();

  @ViewChild(CdkMenuTrigger) invitePeopleDropdownMenuTrigger?: CdkMenuTrigger;
  @ViewChild('invitePeopleInput')
  invitePeopleInput?: fromSeriousSystem.InputComponent;

  public readonly inputValue = signal<string>('');

  protected readonly groupInitial = groupInitial;

  /**
   * The skill owner, shown non-removable at the top of the list. Not looked up
   * in `allUsers`: for a super_admin caller that list omits every super_admin
   * (they are never pickable), the owner included.
   */
  public readonly ownerRow = computed(() => {
    const owner = this.owner();
    if (!owner) return null;
    return {
      initials: owner.initials ?? '-',
      title: owner.fullName ?? owner.email,
      detail: owner.email,
    };
  });

  public readonly selectedUsers = computed(() =>
    this.allUsers().filter((user) => this.selectedUsersIds().includes(user.id)),
  );
  public readonly selectedGroups = computed(() =>
    this.allGroups().filter((group) =>
      this.selectedGroupsIds().includes(group.id),
    ),
  );

  public readonly candidatesFilteredByInputValue = computed<InviteCandidate[]>(
    () => {
      const value = this.inputValue().trim();
      if (!value) return [];

      // Accent-/case-insensitive word-boundary match; haystacks are folded
      // below so both sides compare in the same canonical form.
      const regex = buildWordBoundaryMatcher(value);

      const groupCandidates: InviteCandidate[] = this.allGroups()
        .filter((group) => !this.selectedGroupsIds().includes(group.id))
        .filter((group) => regex.test(foldForSearch(group.name)))
        .map((group) => ({
          kind: 'group',
          id: group.id,
          initials: groupInitial(group.name),
          title: group.name,
          prefix: this.translate.instant(
            'ASSISTANTS.SHARE.ROW_GROUP',
          ) as string,
          detail: this.membersLabel(group.memberCount),
        }));

      const userCandidates: InviteCandidate[] = this.allUsers()
        .filter((user) => user.id !== this.userId())
        .filter((user) => !this.selectedUsersIds().includes(user.id))
        .filter((user) =>
          regex.test(foldForSearch(user.fullName ?? user.email)),
        )
        .map((user) => ({
          kind: 'user',
          id: user.id,
          initials: user.initials ?? '-',
          title: user.fullName ?? user.email,
          prefix: '',
          detail: user.email,
        }));

      return [...groupCandidates, ...userCandidates]
        .sort((a, b) => a.title.localeCompare(b.title))
        .slice(0, MAX_RESULTS_TO_SHOW);
    },
  );

  public membersLabel(count: number): string {
    return this.translate.instant('ASSISTANTS.SHARE.ROW_MEMBERS', {
      count,
    }) as string;
  }

  public handleClickRemoveUserByIdCTA(id: string) {
    this.selectedUsersIds.update((ids) =>
      ids.filter((userId) => userId !== id),
    );
  }

  public handleClickRemoveGroupByIdCTA(id: string) {
    this.selectedGroupsIds.update((ids) =>
      ids.filter((groupId) => groupId !== id),
    );
  }

  public handleInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value ?? '';

    this.inputValue.set(value);

    if (!this.invitePeopleDropdownMenuTrigger) return;

    if (value) {
      this.invitePeopleDropdownMenuTrigger.open();
    } else {
      this.invitePeopleDropdownMenuTrigger.close();
    }
  }

  public handleClickAddCandidateCTA(candidate: InviteCandidate) {
    if (candidate.kind === 'user') {
      this.selectedUsersIds.update((ids) =>
        ids.includes(candidate.id) ? ids : [...ids, candidate.id],
      );
    } else {
      this.selectedGroupsIds.update((ids) =>
        ids.includes(candidate.id) ? ids : [...ids, candidate.id],
      );
    }

    this.inputValue.set('');
    if (this.invitePeopleInput) {
      this.invitePeopleInput.value = '';
    }
    if (this.invitePeopleDropdownMenuTrigger) {
      this.invitePeopleDropdownMenuTrigger.close();
    }
  }
}
